/**
 * B526 (e4) — what an OTA partner itself calls, in OCTO 1.0 core shapes
 * (docs.octo.travel, read 2026-09-29). Every op here admits the RESELLER
 * principal the authorizer exchanged for an `ota1.` key; the handlers refuse
 * a reseller without that stamp. The tenant's own management doors live in
 * ota.ts.
 */
import { z } from "zod";
import { readOp, writeOp, zId } from "./common.js";
import { zOctoUnitType } from "./ota-shared.js";

/* ------------------------------------------------- the partner's own reads */

/**
 * B526 (e4) — what an OTA partner reads, in OCTO 1.0 core shapes
 * (docs.octo.travel, read 2026-09-29). The caller is a RESELLER principal
 * the authorizer exchanged for an `ota1.` key: the partner and its channel
 * come from that principal, never from a request field, so no field here
 * names a partner or a channel. A reseller without the OTA stamp is refused.
 */
const zNullableInt = z.number().int().safe().nullable();

const zOctoUnit = z
  .object({
    id: zId,
    internalName: z.string(),
    reference: z.string().nullable(),
    type: zOctoUnitType,
    requiredContactFields: z.array(z.string()),
    restrictions: z
      .object({
        minAge: z.number().int().safe(),
        maxAge: z.number().int().safe(),
        idRequired: z.boolean(),
        minQuantity: zNullableInt,
        maxQuantity: zNullableInt,
        paxCount: z.number().int().safe(),
        accompaniedBy: z.array(z.string()),
      })
      .strict(),
  })
  .strict();

const zOctoOption = z
  .object({
    id: zId,
    default: z.boolean(),
    internalName: z.string(),
    reference: z.string().nullable(),
    availabilityLocalStartTimes: z.array(z.string()),
    cancellationCutoff: z.string(),
    cancellationCutoffAmount: z.number().int().safe(),
    cancellationCutoffUnit: z.enum(["hour", "minute", "day"]),
    requiredContactFields: z.array(z.string()),
    restrictions: z.object({ minUnits: zNullableInt, maxUnits: zNullableInt }).strict(),
    units: z.array(zOctoUnit),
  })
  .strict();

const zOctoProduct = z
  .object({
    id: zId,
    internalName: z.string(),
    reference: z.string().nullable(),
    locale: z.string(),
    timeZone: z.string(),
    allowFreesale: z.boolean(),
    instantConfirmation: z.boolean(),
    instantDelivery: z.boolean(),
    availabilityRequired: z.boolean(),
    availabilityType: z.enum(["START_TIME", "OPENING_HOURS"]),
    deliveryFormats: z.array(z.enum(["PDF_URL", "QRCODE", "CODE128", "PKPASS_URL"])),
    deliveryMethods: z.array(z.enum(["VOUCHER", "TICKET"])),
    redemptionMethod: z.enum(["DIGITAL", "PRINT", "MANIFEST"]),
    options: z.array(zOctoOption),
  })
  .strict();

const zOctoStatus = z.enum(["AVAILABLE", "FREESALE", "SOLD_OUT", "LIMITED", "CLOSED"]);
const zOpeningHours = z.array(z.object({ from: z.string(), to: z.string() }).strict());

const zOctoAvailability = z
  .object({
    id: zId,
    localDateTimeStart: z.string(),
    localDateTimeEnd: z.string(),
    utcCutoffAt: z.string(),
    allDay: z.boolean(),
    available: z.boolean(),
    status: zOctoStatus,
    vacancies: z.number().int().safe(),
    capacity: z.number().int().safe(),
    maxUnits: zNullableInt,
    openingHours: zOpeningHours,
  })
  .strict();

const zOctoCalendarDay = z
  .object({
    localDate: z.string(),
    available: z.boolean(),
    status: zOctoStatus,
    vacancies: z.number().int().safe(),
    capacity: z.number().int().safe(),
    openingHours: zOpeningHours,
  })
  .strict();

const zLocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const EXAMPLE_OCTO_AVAILABILITY = {
  id: "s-daily#2026-10-02T09:00",
  localDateTimeStart: "2026-10-02T09:00:00-07:00",
  localDateTimeEnd: "2026-10-02T10:00:00-07:00",
  utcCutoffAt: "2026-10-02T15:45:00Z",
  allDay: false,
  available: true,
  status: "AVAILABLE" as const,
  vacancies: 40,
  capacity: 60,
  maxUnits: null,
  openingHours: [],
};

export const octoProducts = readOp({
  name: "octo.products",
  permission: "catalog:read",
  principals: ["RESELLER"],
  fields: { productId: zId.optional() },
  response: z.object({ products: z.array(zOctoProduct) }).strict(),
  exampleRequest: {},
  exampleResponse: { products: [] },
});

export const octoAvailability = readOp({
  name: "octo.availability",
  permission: "supply:read",
  principals: ["RESELLER"],
  fields: {
    productId: zId,
    optionId: zId,
    localDateStart: zLocalDate.optional(),
    localDateEnd: zLocalDate.optional(),
    availabilityIds: z.array(zId).min(1).max(100).optional(),
  },
  response: z.object({ availabilities: z.array(zOctoAvailability) }).strict(),
  exampleRequest: { productId: "harbor-ferry", optionId: "DEFAULT", localDateStart: "2026-10-02", localDateEnd: "2026-10-02" },
  exampleResponse: { availabilities: [EXAMPLE_OCTO_AVAILABILITY] },
});

export const octoAvailabilityCalendar = readOp({
  name: "octo.availability.calendar",
  permission: "supply:read",
  principals: ["RESELLER"],
  fields: { productId: zId, optionId: zId, localDateStart: zLocalDate, localDateEnd: zLocalDate },
  response: z.object({ days: z.array(zOctoCalendarDay) }).strict(),
  exampleRequest: { productId: "harbor-ferry", optionId: "DEFAULT", localDateStart: "2026-10-02", localDateEnd: "2026-10-03" },
  exampleResponse: {
    days: [{ localDate: "2026-10-02", available: true, status: "AVAILABLE" as const, vacancies: 40, capacity: 60, openingHours: [] }],
  },
});

/* ------------------------------------------------- the partner's bookings */

/**
 * B526 (e4) — an OTA booking is an ORDER like any other: reserve walks the
 * real cart checkout on the partner's own channel (holds, frozen totals,
 * Inv 3/4), and the booking row only remembers the partner-facing names. A
 * partner's uuid is its idempotency: reserving the same uuid twice answers
 * the same booking.
 */
const zOtaUuid = z.string().regex(/^[A-Za-z0-9-]{1,64}$/);
const zNullableString = z.string().nullable();
const zOctoBookingStatus = z.enum(["ON_HOLD", "CONFIRMED", "EXPIRED", "CANCELLED", "REDEEMED", "PENDING", "REJECTED"]);

const zOctoContact = z
  .object({
    fullName: z.string().max(200).optional(),
    firstName: z.string().max(100).optional(),
    lastName: z.string().max(100).optional(),
    emailAddress: z.string().max(320).optional(),
    phoneNumber: z.string().max(40).optional(),
    locales: z.array(z.string().max(20)).max(10).optional(),
    country: z.string().max(2).optional(),
    notes: z.string().max(1000).optional(),
  })
  .strict();

const zOctoTicket = z
  .object({
    redemptionMethod: z.literal("DIGITAL"),
    utcRedeemedAt: zNullableString,
    deliveryOptions: z.array(z.object({ deliveryFormat: z.literal("QRCODE"), deliveryValue: z.string() }).strict()),
  })
  .strict();

const zOctoUnitItem = z
  .object({
    uuid: zId,
    resellerReference: zNullableString,
    supplierReference: zNullableString,
    unitId: zId,
    status: zOctoBookingStatus,
    utcRedeemedAt: zNullableString,
    contact: zOctoContact.nullable(),
    ticket: zOctoTicket.nullable(),
  })
  .strict();

const zOctoBooking = z
  .object({
    id: zId,
    uuid: zId,
    testMode: z.boolean(),
    resellerReference: zNullableString,
    supplierReference: zNullableString,
    status: zOctoBookingStatus,
    utcCreatedAt: z.string(),
    utcUpdatedAt: z.string(),
    utcExpiresAt: zNullableString,
    utcRedeemedAt: zNullableString,
    utcConfirmedAt: zNullableString,
    productId: zId,
    optionId: zId,
    cancellable: z.boolean(),
    cancellation: z
      .object({ refund: z.enum(["FULL", "PARTIAL", "NONE"]), reason: zNullableString, utcCancelledAt: z.string() })
      .strict()
      .nullable(),
    freesale: z.literal(false),
    availabilityId: zId,
    availability: z
      .object({ id: zId, localDateTimeStart: z.string(), localDateTimeEnd: z.string(), allDay: z.boolean(), openingHours: zOpeningHours })
      .strict(),
    contact: zOctoContact.nullable(),
    notes: zNullableString,
    deliveryMethods: z.array(z.literal("TICKET")),
    voucher: z.null(),
    unitItems: z.array(zOctoUnitItem),
  })
  .strict();

const EXAMPLE_BOOKING = {
  id: "b1f0c6c2-6a5e-4b8e-9e9a-0d5f1b2c3d4e",
  uuid: "b1f0c6c2-6a5e-4b8e-9e9a-0d5f1b2c3d4e",
  testMode: false,
  resellerReference: null,
  supplierReference: "order-ota-klook-b1f0c6c2-6a5e-4b8e-9e9a-0d5f1b2c3d4e",
  status: "ON_HOLD" as const,
  utcCreatedAt: "2026-10-01T12:00:00Z",
  utcUpdatedAt: "2026-10-01T12:00:00Z",
  utcExpiresAt: "2026-10-01T12:10:00Z",
  utcRedeemedAt: null,
  utcConfirmedAt: null,
  productId: "harbor-ferry",
  optionId: "DEFAULT",
  cancellable: true,
  cancellation: null,
  freesale: false as const,
  availabilityId: "s-daily#2026-10-02T09:00",
  availability: {
    id: "s-daily#2026-10-02T09:00",
    localDateTimeStart: "2026-10-02T09:00:00-07:00",
    localDateTimeEnd: "2026-10-02T10:00:00-07:00",
    allDay: false,
    openingHours: [],
  },
  contact: null,
  notes: null,
  deliveryMethods: ["TICKET" as const],
  voucher: null,
  unitItems: [
    { uuid: "u-1", resellerReference: null, supplierReference: null, unitId: "adult", status: "ON_HOLD" as const, utcRedeemedAt: null, contact: null, ticket: null },
  ],
};

export const octoBookingReserve = writeOp({
  name: "octo.booking.reserve",
  permission: "order:write",
  principals: ["RESELLER"],
  fields: {
    uuid: zOtaUuid.optional(),
    productId: zId,
    optionId: zId,
    availabilityId: zId,
    expirationMinutes: z.number().int().safe().optional(),
    notes: z.string().max(1000).optional(),
    currency: z.string().regex(/^[A-Z]{3}$/).optional(),
    unitItems: z.array(z.object({ uuid: zOtaUuid.optional(), unitId: zId }).strict()).min(1).max(50),
  },
  response: z.object({ booking: zOctoBooking }).strict(),
  exampleRequest: { uuid: EXAMPLE_BOOKING.uuid, productId: "harbor-ferry", optionId: "DEFAULT", availabilityId: "s-daily#2026-10-02T09:00", unitItems: [{ unitId: "adult" }] },
  exampleResponse: { booking: EXAMPLE_BOOKING },
});

export const octoBookingGet = readOp({
  name: "octo.booking.get",
  permission: "order:read",
  principals: ["RESELLER"],
  fields: { uuid: zOtaUuid },
  response: z.object({ booking: zOctoBooking }).strict(),
  exampleRequest: { uuid: EXAMPLE_BOOKING.uuid },
  exampleResponse: { booking: EXAMPLE_BOOKING },
});

/**
 * B526 (e4) slice 5b — confirm: the partner settles by invoice, so the hold
 * is paid by an `on_account` tender for the frozen total through the SAME
 * confirmation path staff tenders walk (Inv 5: one confirmation per order),
 * with ruling (c)'s commission journalled beside the price. Confirming a
 * confirmed booking answers it again; an expired hold is refused.
 */
export const octoBookingConfirm = writeOp({
  name: "octo.booking.confirm",
  permission: "payment:write",
  principals: ["RESELLER"],
  fields: { uuid: zOtaUuid, contact: zOctoContact.optional(), resellerReference: z.string().min(1).max(100).optional() },
  response: z.object({ booking: zOctoBooking }).strict(),
  exampleRequest: { uuid: EXAMPLE_BOOKING.uuid, resellerReference: "KL-77812" },
  exampleResponse: { booking: { ...EXAMPLE_BOOKING, status: "CONFIRMED" as const, utcExpiresAt: null, utcConfirmedAt: "2026-10-01T12:02:00Z", resellerReference: "KL-77812" } },
});

/**
 * B526 (e4) slice 5c — cancel on the e3 plan: a hold is released (seats back);
 * a confirmed booking inside its option's cutoff is refunded in full through
 * the staff refund path, tickets voided, the commission reversed beside it.
 * Past the cutoff, scanned, or partly refunded at the desk → refused.
 */
export const octoBookingCancel = writeOp({
  name: "octo.booking.cancel",
  permission: "order:write",
  principals: ["RESELLER"],
  fields: { uuid: zOtaUuid, reason: z.string().min(1).max(500).optional() },
  response: z.object({ booking: zOctoBooking }).strict(),
  exampleRequest: { uuid: EXAMPLE_BOOKING.uuid, reason: "Customer changed plans" },
  exampleResponse: {
    booking: { ...EXAMPLE_BOOKING, status: "CANCELLED" as const, utcExpiresAt: null, cancellable: false, cancellation: { refund: "FULL" as const, reason: "Customer changed plans", utcCancelledAt: "2026-10-01T12:05:00Z" } },
  },
});
